/**
 * Clientes.
 *
 * QUE PREGUNTA CONTESTA ESTA PANTALLA
 * -----------------------------------
 * "¿Quien me debe, cuanto, y desde cuando?" — y despues, para uno en concreto,
 * "¿cuanto le puedo seguir fiando y que pasó en su cuenta?".
 *
 * LO UNICO IRREVERSIBLE QUE SE HACE ACA
 * -------------------------------------
 * Dar de alta un cliente elige el ANCLAJE de su cuenta corriente: si la deuda
 * queda fija en pesos o si sigue al dolar. Es la unica decision de todo el
 * modulo que despues no se puede cambiar —el backend devuelve 409 apenas la
 * cuenta tiene un movimiento— y es la que menos parece una decision cuando se
 * la escribe como "cc_modo: FIJA | USD".
 *
 * Por eso el formulario no pregunta el modo: pregunta la consecuencia, con un
 * ejemplo en pesos y con el dolar real del sistema. Ver `ExplicadorAnclaje`.
 *
 * DOS VISTAS: «ME DEBEN» Y «TODOS»
 * --------------------------------
 * Clientes y Cuentas corrientes eran dos secciones que contestaban lo mismo, y
 * se cosian a mano: el «Cobrarle» de la ficha mandaba a la otra pantalla solo
 * para abrir un dialogo. Ahora es una: «Me deben» (por defecto, lo que era
 * Cuentas corrientes, ordenado por mora y con el cobro en la fila) y «Todos»
 * (el directorio, con el alta). Las dos abren la misma ficha, y la ficha cobra
 * ahi mismo. `/cuentas-corrientes` redirige aca.
 *
 * DOS NUMEROS, NO UNO
 * -------------------
 * Un cliente con cuenta anclada al dolar debe dolares y se le cobran pesos. Los
 * dos numeros estan a la vista en la ficha, y al lado del de pesos siempre esta
 * la cotizacion con la que se llego. Nunca se suma una moneda con la otra.
 */
import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Plus, Users } from "lucide-react";

import { CobroCliente } from "@/components/cobro/CobroCliente";
import { PanelDeudores } from "@/components/cobro/PanelDeudores";
import type { DeudorFila } from "@/components/cobro/logica";

import { PageHeader } from "@/components/layout/PageHeader";
import { Pagina } from "@/components/layout/Pagina";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Cargando } from "@/components/ui/cargando";
import { EstadoVacio } from "@/components/ui/estado-vacio";
import { PieDeLista } from "@/components/ui/pie-de-lista";
import { Segmented } from "@/components/ui/segmented";
import { DialogCliente } from "@/components/clientes/DialogCliente";
import { Extracto } from "@/components/clientes/Extracto";
import { FichaCliente } from "@/components/clientes/FichaCliente";
import { ListaClientes } from "@/components/clientes/ListaClientes";
import {
  armarFilas,
  consultaSaldosDelDirectorio,
  impactoDeCambiarAnclaje,
  leerLineaExtracto,
  monedaAnclaje,
  ordenarPorDeuda,
  type MoraCC,
  type SaldoCC,
} from "@/components/clientes/logica";
import {
  useActualizarCliente,
  useCliente,
  useClientes,
  useConfig,
  useCrearCliente,
  useDeudas,
  useExtracto,
} from "@/lib/queries";
import { mostrarError, mostrarExito } from "@/lib/errores";
import { useVerMas } from "@/lib/usar-ver-mas";
import { aNumero, type Cliente } from "@/lib/tipos";

type Vista = "deben" | "todos";

export interface BusquedaClientes {
  /** La ficha abierta. El buscador global enlaza a un cliente con `?id=`. */
  id?: number;
  /** Sin `vista` es «Me deben»: es a lo que se entra la mayoria de las veces. */
  vista?: "todos";
  /** Con `id`: abrir el cobro de ese cliente apenas carga la ficha. */
  cobrar?: 1;
}

export const Route = createFileRoute("/clientes")({
  component: Clientes,
  // Se valida en vez de confiar: la URL la puede tipear cualquiera.
  validateSearch: (busqueda: Record<string, unknown>): BusquedaClientes => {
    const id = Number(busqueda.id);
    const conId = Number.isInteger(id) && id > 0;
    return {
      ...(conId ? { id } : {}),
      ...(busqueda.vista === "todos" ? { vista: "todos" as const } : {}),
      ...(conId && Number(busqueda.cobrar) === 1 ? { cobrar: 1 as const } : {}),
    };
  },
});

const VISTAS = [
  { valor: "deben", etiqueta: "Me deben" },
  { valor: "todos", etiqueta: "Todos" },
] as const;

/** Cuantos clientes y cuantos movimientos se traen por pagina. */
const POR_PAGINA = 200;

function Clientes(): JSX.Element {
  const { id, vista: vistaUrl, cobrar } = Route.useSearch();
  const vista: Vista = vistaUrl ?? "deben";
  const navegar = useNavigate({ from: Route.fullPath });

  const [texto, setTexto] = useState("");
  const [dialogo, setDialogo] = useState<"cerrado" | "alta" | "edicion">("cerrado");
  const [cobrando, setCobrando] = useState<DeudorFila | null>(null);

  /*
    LA BUSQUEDA VA A LA BASE.
    -------------------------
    Antes se filtraba en el navegador sobre los 200 clientes ya traidos, asi
    que a partir del cliente 201 buscar a alguien que existe daba "no lo
    encontre". Es el mismo bug que ya se arreglo en Ventas. La base busca por
    nombre, codigo, documento, telefono y email —y sin acentos, como filtraba
    el navegador— asi que no se perdio ninguna forma de encontrar a alguien.
  */
  const busqueda = texto.trim();
  const { limite, verMas, reiniciar } = useVerMas(POR_PAGINA);
  useEffect(() => reiniciar(), [busqueda, reiniciar]);

  // El directorio solo se pide en «Todos»: «Me deben» trae su propia lista.
  const enTodos = vista === "todos" && id === undefined;
  const { data: clientes, isFetching: cargandoLista } = useClientes(
    { limite, ...(busqueda ? { q: busqueda } : {}) },
    { enabled: enTodos },
  );
  // Las deudas se piden con la misma busqueda: son el saldo de estas mismas
  // filas, y traer las de otros clientes no agrega nada a la lista. Van con
  // `todos`: ver `consultaSaldosDelDirectorio`.
  const { data: deudas } = useDeudas(consultaSaldosDelDirectorio(limite, busqueda), {
    enabled: enTodos,
  });
  const { data: config } = useConfig();
  const { data: ficha, isError: fichaFallo, error: errorFicha } = useCliente(id);
  const { data: extracto, isFetching: cargandoExtracto } = useExtracto(id, {
    limite: POR_PAGINA,
  });

  const cotizacion = aNumero(config?.cotizacion_vigente);

  const filas = useMemo(
    () => ordenarPorDeuda(armarFilas(clientes?.filas ?? [], deudas?.filas ?? [])),
    [clientes, deudas],
  );

  const cliente: Cliente | null = ficha?.cliente ?? null;
  const saldo: SaldoCC | null = ficha?.saldo ?? null;
  const mora: MoraCC | null = ficha?.mora ?? null;

  // Con un solo movimiento el anclaje ya quedo fijo. El dato sale de la vista de
  // saldos; si no vino, se cae a cuantas lineas trajo el extracto.
  const movimientos =
    saldo?.movimientos !== undefined ? aNumero(saldo.movimientos) : (extracto?.total ?? 0);

  const lineas = useMemo(() => {
    if (!cliente) return [];
    const anclaje = monedaAnclaje(cliente);
    return (extracto?.filas ?? []).map((f) => leerLineaExtracto(f, anclaje));
  }, [extracto, cliente]);

  const crear = useCrearCliente();
  const actualizar = useActualizarCliente();

  // La vista viaja con la ficha: «Volver a la lista» vuelve a la que se venia.
  const conVista = vistaUrl ? { vista: vistaUrl } : {};
  function seleccionar(nuevoId: number): void {
    void navegar({ search: { ...conVista, id: nuevoId } });
  }
  function volverALaLista(): void {
    void navegar({ search: conVista });
  }
  function cambiarVista(v: Vista): void {
    void navegar({ search: v === "todos" ? { vista: "todos" } : {} });
  }

  // `mora` es la fila de `v_mora_clientes`: la misma que trae la lista de
  // deudores, asi que el cobro desde la ficha es identico al de la lista.
  const cobrable = mora && aNumero(mora.saldo_ars_hoy ?? 0) > 0.009 ? (mora as DeudorFila) : null;

  /**
   * Llegar con `?cobrar=1` (lo que manda el viejo `/cuentas-corrientes?cliente=`)
   * abre el cobro en cuanto hay ficha. Se saca el parametro al abrir: si no,
   * cerrar el dialogo y recargar lo volveria a abrir, y "atras" tambien.
   */
  useEffect(() => {
    if (!cobrar || !ficha) return;
    if (cobrable) setCobrando(cobrable);
    void navegar({ search: { ...conVista, id }, replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo al llegar la ficha
  }, [cobrar, ficha]);

  const hayFicha = id !== undefined;

  return (
    /*
      UNA PANTALLA POR VEZ, NO DOS COLUMNAS
      -------------------------------------
      Antes esto era un split: la lista a la izquierda en el 40% y la ficha a la
      derecha. Las dos cosas salian mal al mismo tiempo. La lista es una tabla de
      cinco columnas que en 460px no entra —Saldo y Estado, que son las dos
      unicas por las que alguien abre Clientes, se iban atras de un scroll
      horizontal— y la mitad derecha estaba vacia el 100% del tiempo hasta que
      alguien tocaba una fila.

      Ahora es lo que ya era en el celular a todo ancho: buscador, lista, y al
      elegir un cliente la lista se va y queda su ficha con el boton de volver.
      El estado vive en la URL (`?id=`), asi que el back del navegador tambien
      vuelve a la lista y un cliente puntual se sigue pudiendo enlazar.

      `alto="fijo"` solo en la lista: la pagina no scrollea, scrollea la tabla,
      que ahora se lleva todo el alto en vez de un techo de 36rem con la pagina
      scrolleando por detras. La ficha si scrollea entera: el extracto es largo y
      no es una lista que se recorra, es algo que se lee.
    */
    <Pagina alto={enTodos ? "fijo" : "natural"}>
      {hayFicha ? (
        <div>
          <Button type="button" variant="ghost" className="-ml-3" onClick={volverALaLista}>
            <ArrowLeft aria-hidden="true" />
            Volver a la lista
          </Button>
        </div>
      ) : (
        <>
          <PageHeader
            titulo="Clientes"
            icono={Users}
            acciones={
              <Button type="button" onClick={() => setDialogo("alta")}>
                <Plus aria-hidden="true" />
                Cliente nuevo
              </Button>
            }
          />
          <Segmented
            etiqueta="Qué clientes ver"
            opciones={VISTAS}
            valor={vista}
            onChange={cambiarVista}
            className="self-start"
          />
        </>
      )}

      {!hayFicha && vista === "deben" ? (
        <PanelDeudores onCobrar={setCobrando} onAbrir={seleccionar} />
      ) : !hayFicha ? (
        <ListaClientes
          filas={filas}
          seleccionadoId={id ?? null}
          onSeleccionar={seleccionar}
          texto={texto}
          onTexto={setTexto}
          cargando={cargandoLista}
          total={clientes?.total ?? 0}
          pie={
            <PieDeLista
              visibles={filas.length}
              total={clientes?.total ?? 0}
              sustantivo="clientes"
              onVerMas={verMas}
              cargando={cargandoLista}
            />
          }
        />
      ) : fichaFallo ? (
        <EstadoVacio
          titulo="No pudimos abrir esa ficha"
          detalle={errorFicha?.mensaje ?? "El cliente no existe o se dio de baja."}
          accion={
            <Button asChild type="button" variant="outline">
              <Link to="/clientes" search={conVista}>
                Volver a la lista
              </Link>
            </Button>
          }
        />
      ) : cliente ? (
        <>
          <FichaCliente
            cliente={cliente}
            saldo={saldo}
            mora={mora}
            cotizacion={cotizacion}
            onEditar={() => setDialogo("edicion")}
            onCobrar={cobrable ? () => setCobrando(cobrable) : undefined}
          />
          <Extracto
            lineas={lineas}
            moneda={monedaAnclaje(cliente)}
            total={extracto?.total}
            cargando={cargandoExtracto}
            reservadoTaller={aNumero(saldo?.senas_ot ?? 0)}
          />
        </>
      ) : (
        <Card padding="none">
          <Cargando texto="Cargando la ficha…" />
        </Card>
      )}

      <CobroCliente cliente={cobrando} onCerrar={() => setCobrando(null)} />

      <DialogCliente
        abierto={dialogo !== "cerrado"}
        onAbiertoChange={(a) => setDialogo(a ? (cliente ? "edicion" : "alta") : "cerrado")}
        cliente={dialogo === "edicion" ? cliente : null}
        movimientos={movimientos}
        impacto={impactoDeCambiarAnclaje(saldo, cotizacion)}
        cotizacion={cotizacion}
        guardando={crear.isPending || actualizar.isPending}
        onCrear={(datos) =>
          crear.mutate(datos, {
            onSuccess: (nuevo) => {
              setDialogo("cerrado");
              mostrarExito(
                `${nuevo.nombre} quedó cargado`,
                monedaAnclaje(nuevo) === "USD"
                  ? "Su cuenta corriente sigue al dólar."
                  : "Su cuenta corriente queda fija en pesos.",
              );
              seleccionar(nuevo.id);
            },
            onError: mostrarError,
          })
        }
        onActualizar={(cambios) => {
          if (!cliente) return;
          actualizar.mutate(
            { id: cliente.id, cambios },
            {
              onSuccess: () => {
                setDialogo("cerrado");
                mostrarExito("Cliente actualizado");
              },
              // El 409 de `CC_CON_MOVIMIENTOS` llega con un texto que explica el
              // problema entero y como seguir. Se muestra tal cual y el diálogo
              // queda abierto: hay algo para corregir ahí adentro.
              onError: mostrarError,
            },
          );
        }}
      />
    </Pagina>
  );
}
